import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import { ToastProvider } from "@/components/Toast";
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Register from "./pages/Register";
import PageLoader from "@/components/PageLoader";
import RingModeManager from "@/components/RingModeManager";
import SiteGate from "@/components/SiteGate";
import { SiteConfigProvider } from "@/lib/siteConfig";
import { dropPushIfLoggedOut } from '@/lib/push';
import { writeSessionFlag, isLoggedIn, getUser, api } from '@/lib/auth';
import { captureAttribution, currentUrlCampaign } from '@/lib/attribution';
import { initGA, gaPageView } from '@/lib/analytics';
import { useSiteConfig } from '@/lib/siteConfig';

// Split out of the initial bundle — these are only needed once someone is
// logged in or has followed a QR link, not on first paint for every visitor.
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Messages = lazy(() => import("./pages/Messages"));
const History = lazy(() => import("./pages/History"));
const Plans = lazy(() => import("./pages/Plans"));
const Admin = lazy(() => import("./pages/Admin"));
const Marketing = lazy(() => import("./pages/Marketing"));
const Demo = lazy(() => import("./pages/Demo"));
const Legal = lazy(() => import("./pages/Legal"));
const LabelPrint = lazy(() => import("./pages/LabelPrint"));
const PublicPage = lazy(() => import("./pages/PublicPage"));
const Blog = lazy(() => import("./pages/Blog"));
const BusinessPlan = lazy(() => import("./pages/BusinessPlan"));
const PartnerDashboard = lazy(() => import("./pages/PartnerDashboard"));
const Activate = lazy(() => import("./pages/Activate"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Reports each page the visitor opens (for the admin Visits / Traffic views).
// Skips the admin area and admin accounts; fire-and-forget, never blocks the UI.
let firstViewInThisLoad = true;
const IS_PRERENDER = /HeadlessChrome/.test(navigator.userAgent);   // build-time snapshot run
const PageViewTracker = () => {
  const { pathname, search } = useLocation();
  useEffect(() => {
    captureAttribution();                       // also on in-app navigation (e.g. finder page -> /car?utm_...)
    if (IS_PRERENDER || pathname.startsWith('/admin') || getUser()?.is_admin) return;
    let visitor = null;
    try {
      visitor = localStorage.getItem('dl_vid');
      if (!visitor) {
        visitor = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now());
        localStorage.setItem('dl_vid', visitor);
      }
    } catch { /* storage blocked: still count the view, just not as a returning visitor */ }
    api.post('/analytics/pageview', {
      path: pathname,
      referrer: firstViewInThisLoad ? document.referrer : '',     // only the landing view has an outside referrer
      visitor_id: visitor,
      standalone: Boolean(window.matchMedia?.('(display-mode: standalone)').matches),
      ...currentUrlCampaign(),
    }).catch(() => {});
    firstViewInThisLoad = false;
  }, [pathname, search]);
  return null;
};

// Google Analytics: loads once an admin has set a Measurement ID. Never tracks admin accounts, the
// admin area, the public demo tags, or the build-time prerender.
const AnalyticsBridge = () => {
  const { config } = useSiteConfig();
  const { pathname, search } = useLocation();
  const id = config.analytics?.ga_measurement_id;
  const allowed = Boolean(id) && !IS_PRERENDER && !getUser()?.is_admin && !pathname.startsWith('/admin') && !pathname.startsWith('/scan/DEMO-');
  useEffect(() => { if (allowed) initGA(id); }, [allowed, id]);
  useEffect(() => { if (allowed) gaPageView(pathname + search); }, [allowed, pathname, search]);
  return null;
};

function App() {
  useEffect(() => {
    // A logged-out browser must not keep receiving call/scan alerts.
    writeSessionFlag(isLoggedIn());
    dropPushIfLoggedOut();

    // Register service worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then(registration => console.log('SW registered:', registration))
          .catch(error => console.log('SW registration failed:', error));
      });
    }

    // Test backend connection
    if (!IS_PRERENDER) axios.get(`${BACKEND_URL}/`)
      .then(response => console.log('Backend connected:', response.data.app))
      .catch(error => console.error('Backend connection error:', error));
  }, []);

  return (
    <div className="App">
      <ToastProvider>
       <SiteConfigProvider>
        <BrowserRouter>
          <RingModeManager />
          <PageViewTracker />
          <AnalyticsBridge />
          <SiteGate>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/history" element={<History />} />
              <Route path="/label/:type/:id" element={<LabelPrint />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/demo" element={<Demo />} />
              <Route path="/terms" element={<Legal page="terms" />} />
              <Route path="/privacy" element={<Legal page="privacy" />} />
              <Route path="/refund" element={<Legal page="refund" />} />
              <Route path="/shipping" element={<Legal page="shipping" />} />
              <Route path="/contact" element={<Legal page="contact" />} />
              <Route path="/car" element={<Marketing slug="car" />} />
              <Route path="/pet" element={<Marketing slug="pet" />} />
              <Route path="/society" element={<Marketing slug="society" />} />
              <Route path="/bike" element={<Marketing slug="bike" />} />
              <Route path="/assets" element={<Marketing slug="assets" />} />
              <Route path="/partners" element={<Marketing slug="partners" />} />
              <Route path="/partners/vets" element={<Marketing slug="vets" />} />
              <Route path="/partners/car-wash" element={<Marketing slug="carwash" />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<Blog />} />
              <Route path="/business" element={<BusinessPlan />} />
              <Route path="/partner" element={<PartnerDashboard />} />
              <Route path="/activate/:qrId" element={<Activate />} />
              <Route path="/scan/:qrId" element={<PublicPage />} />
              <Route path="/public/:qrId" element={<PublicPage />} />
            </Routes>
          </Suspense>
          </SiteGate>
        </BrowserRouter>
       </SiteConfigProvider>
      </ToastProvider>
    </div>
  );
}

export default App;
